import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, Sparkles, Lock, CheckCircle2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const input = "w-full h-[50px] rounded-2xl bg-[#FAF9F5] border border-[#1A1A1A]/5 px-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-accent/70 focus:bg-white transition-colors placeholder:text-stone-400";
const label = "text-[11px] uppercase tracking-widest font-semibold text-stone-500 block mb-1.5";
const btn = "w-full h-[52px] bg-accent text-white font-bold rounded-full hover:bg-[#E85A24] active:scale-[0.99] transition-all disabled:opacity-50";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function GoogleButton({ isLogin }) {
  return (
    <button type="button" data-testid="google-auth-btn" onClick={startGoogleLogin}
      className="w-full h-[52px] bg-[#1A1A1A] text-white rounded-full font-bold text-[15px] flex items-center justify-center gap-3 hover:bg-black active:scale-[0.99] transition-all">
      <span className="bg-white rounded-full w-7 h-7 flex items-center justify-center shrink-0">
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
      </span>
      {isLogin ? "Mit Google anmelden" : "Mit Google registrieren"}
    </button>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => {
        setUser(data);
        toast.success(data.is_new_user ? "Willkommen bei KLEDIO!" : "Willkommen zurück!");
        nav("/", { replace: true });
      })
      .catch(() => {
        toast.error("Google-Anmeldung fehlgeschlagen. Bitte versuche es erneut.");
        nav("/anmelden", { replace: true });
      });
  }, [nav, setUser]);
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4" data-testid="google-auth-callback">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Anmeldung mit Google …</p>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, testid, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} className={`${input} pr-12`} type={show ? "text" : "password"}
        placeholder={placeholder} value={value} onChange={onChange} required minLength={8} autoComplete={autoComplete} />
      <button type="button" aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"} tabIndex={-1}
        data-testid={`${testid}-toggle`} onClick={() => setShow(!show)}
        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
        {show ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
      </button>
    </div>
  );
}

function PasswordStrength({ password }) {
  const checks = [
    ["Mindestens 8 Zeichen", password.length >= 8],
    ["Großbuchstabe", /[A-ZÄÖÜ]/.test(password)],
    ["Zahl", /\d/.test(password)],
    ["Sonderzeichen", /[^A-Za-z0-9äöüÄÖÜß]/.test(password)],
  ];
  const score = checks.filter(([, ok]) => ok).length;
  const levels = ["", "Schwach", "Mittel", "Gut", "Stark"];
  const colors = ["bg-border", "bg-red-400", "bg-amber-400", "bg-blue-500", "bg-emerald-500"];
  if (!password) return null;
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1.5">
        {[1, 2, 3, 4].map((i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${score >= i ? colors[score] : "bg-border"}`} />)}
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">Passwortstärke: <span className="font-semibold text-foreground">{levels[score]}</span></p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1">
        {checks.map(([txt, ok]) => (
          <span key={txt} className={`text-[11px] flex items-center gap-1 ${ok ? "text-emerald-600" : "text-muted-foreground"}`}>
            {ok ? "✓" : "○"} {txt}
          </span>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Erforderlich sind mindestens 8 Zeichen — der Rest macht dein Konto sicherer.</p>
    </div>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-[calc(100vh-56px)] bg-[#FAFAF8] relative overflow-hidden flex items-start justify-center px-4 pt-6 sm:pt-12 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:pb-16">
      <div className="pointer-events-none absolute inset-0 select-none" aria-hidden="true">
        <div className="absolute -top-10 left-[6%] w-44 h-44 border-2 border-dashed border-accent/25 rounded-full hidden md:block" />
        <div className="absolute bottom-20 right-[8%] w-28 h-28 bg-accent/10 rounded-[2rem] rotate-12 hidden md:block" />
        <div className="absolute top-1/3 left-[3%] w-16 h-16 bg-[#1A1A1A]/5 rounded-2xl -rotate-12 hidden lg:block" />
        <span className="absolute top-[16%] right-[5%] hidden lg:block -rotate-6 bg-[#1A1A1A] text-white text-[10px] font-bold tracking-[0.22em] px-3.5 py-1.5 rounded-md">0 % VERKÄUFERGEBÜHR</span>
        <span className="absolute bottom-[12%] left-[4%] hidden lg:block rotate-3 bg-accent text-white text-[10px] font-bold tracking-[0.22em] px-3.5 py-1.5 rounded-md">KÄUFERSCHUTZ INKLUSIVE</span>
      </div>
      <div className="w-full max-w-xl relative z-10 auth-card-in">
        <div className="flex items-center justify-center mb-5">
          <span className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] uppercase text-stone-500 bg-white border border-[#1A1A1A]/10 rounded-full px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> Dein Marktplatz
          </span>
        </div>
        <div className="bg-white border border-[#1A1A1A]/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.07)] p-6 sm:p-10 pb-0 sm:pb-0 relative overflow-hidden">
          <span className="absolute top-4 right-4 rotate-6 border border-accent/30 bg-accent/5 text-accent text-[9px] font-bold tracking-[0.18em] px-2.5 py-1 rounded-md hidden sm:block" aria-hidden="true">STRIPE · SICHER</span>
          {children}
          <TrustRibbon />
        </div>
      </div>
    </div>
  );
}

function TrustRibbon() {
  return (
    <div className="mt-8 -mx-6 sm:-mx-10 px-6 sm:px-10 py-3.5 border-t border-dashed border-[#1A1A1A]/15 bg-[#FAF9F5] flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5" data-testid="trust-ribbon">
      <span className="text-[11px] font-semibold text-stone-600 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-accent" /> Käuferschutz</span>
      <span className="text-[11px] font-semibold text-stone-600 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-accent" /> Sichere Stripe-Zahlung</span>
      <span className="text-[11px] font-semibold text-stone-600 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-accent" /> 0 % Verkäufergebühr</span>
    </div>
  );
}

function ModeSwitch({ isLogin }) {
  const nav = useNavigate();
  const location = useLocation();
  const go = (path) => nav({ pathname: path, search: location.search });
  return (
    <div className="relative grid grid-cols-2 bg-[#F3F3EF] rounded-full p-1" data-testid="auth-mode-switch">
      <span aria-hidden="true"
        className={`absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-out ${isLogin ? "translate-x-full" : "translate-x-0"}`} />
      <button type="button" data-testid="mode-switch-register" onClick={() => go("/registrieren")}
        className={`relative z-10 h-10 rounded-full text-sm font-bold transition-colors ${!isLogin ? "text-[#1A1A1A]" : "text-stone-400"}`}>Registrieren</button>
      <button type="button" data-testid="mode-switch-login" onClick={() => go("/anmelden")}
        className={`relative z-10 h-10 rounded-full text-sm font-bold transition-colors ${isLogin ? "text-[#1A1A1A]" : "text-stone-400"}`}>Anmelden</button>
    </div>
  );
}

export default function Auth({ mode }) {
  const [form, setForm] = useState({ email: "", password: "", confirm: "", username: "", first_name: "", last_name: "" });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && form.password !== form.confirm) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register",
        isLogin ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, username: form.username,
              name: `${form.first_name} ${form.last_name}`.trim() });
      setUser(data);
      if (isLogin) {
        toast.success("Willkommen zurück!");
        nav(params.get("next") || "/");
      } else {
        setRegistered(true);
      }
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (registered) {
    return (
      <AuthShell>
        <div className="text-center py-4" data-testid="register-success">
          <div className="w-16 h-16 bg-accent/10 text-accent rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-5">Fast geschafft!</h1>
          <p className="text-sm text-muted-foreground mt-2">Wir haben dir eine E-Mail zur Bestätigung deiner Adresse gesendet. Du kannst KLEDIO schon jetzt entdecken.</p>
          <button data-testid="register-continue-btn" onClick={() => nav(params.get("next") || "/")} className={`${btn} mt-6`}>Weiter zu KLEDIO</button>
          <button data-testid="resend-verification-btn" onClick={async () => {
            try { await api.post("/auth/resend-verification"); toast.success("Eine neue Bestätigungs-E-Mail wurde gesendet."); }
            catch (e) { toast.error(errText(e)); }
          }} className="mt-3 mb-2 text-sm font-semibold text-accent hover:underline block mx-auto">E-Mail erneut senden</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <ModeSwitch isLogin={isLogin} />
      <h1 className="font-display text-2xl sm:text-[32px] font-extrabold tracking-tight leading-tight mt-6">
        {isLogin ? <>Schön, dich<br className="sm:hidden" /> wiederzusehen.</> : <>Dein Platz im<br className="sm:hidden" /> <span className="text-accent">Kleiderkreislauf.</span></>}
      </h1>
      <p className="text-sm text-stone-500 mt-1.5">
        {isLogin ? "Melde dich an und mach da weiter, wo du aufgehört hast." : "Kostenlos registrieren — verkaufen ohne Gebühren, kaufen mit Schutz."}
      </p>

      <div className="mt-6">
        <GoogleButton isLogin={isLogin} />
      </div>
      <div className="flex items-center gap-3 my-5">
        <span className="h-px bg-[#1A1A1A]/10 flex-1" />
        <span className="text-[11px] uppercase tracking-widest text-stone-400 font-semibold">oder mit E-Mail</span>
        <span className="h-px bg-[#1A1A1A]/10 flex-1" />
      </div>

      <form onSubmit={submit} className="space-y-4" noValidate={false}>
        {!isLogin && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label} htmlFor="fn">Vorname</label>
                <input id="fn" data-testid="register-firstname-input" className={input} placeholder="Max" value={form.first_name} onChange={set("first_name")} required autoComplete="given-name" /></div>
              <div><label className={label} htmlFor="ln">Nachname</label>
                <input id="ln" data-testid="register-lastname-input" className={input} placeholder="Muster" value={form.last_name} onChange={set("last_name")} required autoComplete="family-name" /></div>
            </div>
            <div><label className={label} htmlFor="un">Benutzername</label>
              <input id="un" data-testid="register-username-input" className={input} placeholder="dein_username" value={form.username} onChange={set("username")} required minLength={3} autoComplete="username" /></div>
          </>
        )}
        <div><label className={label} htmlFor="em">E-Mail-Adresse</label>
          <input id="em" data-testid="auth-email-input" className={input} type="email" placeholder="deine@email.de" value={form.email} onChange={set("email")} required autoComplete="email" /></div>
        <div>
          <label className={label} htmlFor="pw">Passwort</label>
          <PasswordInput value={form.password} onChange={set("password")} placeholder={isLogin ? "Dein Passwort" : "Mindestens 8 Zeichen"}
            testid="auth-password-input" autoComplete={isLogin ? "current-password" : "new-password"} />
          {!isLogin && <PasswordStrength password={form.password} />}
        </div>
        {!isLogin && (
          <div>
            <label className={label} htmlFor="pw2">Passwort bestätigen</label>
            <PasswordInput value={form.confirm} onChange={set("confirm")} placeholder="Passwort wiederholen" testid="auth-confirm-input" autoComplete="new-password" />
            {form.confirm && form.confirm !== form.password && (
              <p className="text-xs text-red-600 mt-1.5" data-testid="password-mismatch">Die Passwörter stimmen nicht überein.</p>
            )}
          </div>
        )}
        {isLogin && (
          <div className="text-right -mt-1">
            <Link to="/passwort-vergessen" data-testid="forgot-password-link" className="text-sm font-semibold text-accent hover:underline">Passwort vergessen?</Link>
          </div>
        )}
        {!isLogin && (
          <label className="flex items-start gap-2.5 text-sm cursor-pointer bg-[#FAF9F5] rounded-2xl p-3.5">
            <input type="checkbox" data-testid="terms-checkbox" className="mt-0.5 w-4 h-4 accent-[#FF6B35]" checked={terms} onChange={(e) => setTerms(e.target.checked)} required />
            <span className="text-foreground/80">Ich akzeptiere die <Link to="/agb" className="text-accent font-semibold hover:underline">Nutzungsbedingungen</Link> und <Link to="/datenschutz" className="text-accent font-semibold hover:underline">Datenschutzerklärung</Link>.</span>
          </label>
        )}
        <button data-testid="auth-submit-btn" disabled={busy || (!isLogin && !terms)} className={btn}>
          {busy ? (isLogin ? "Einloggen …" : "Konto wird erstellt …") : (isLogin ? "Einloggen" : "Kostenlos registrieren")}
        </button>
      </form>
      <p className="mt-5 mb-1 text-sm text-muted-foreground text-center">
        {isLogin ? (
          <>Noch kein KLEDIO Konto? <Link to="/registrieren" data-testid="switch-to-register" className="text-accent font-bold hover:underline">Jetzt registrieren</Link></>
        ) : (
          <>Bereits registriert? <Link to="/anmelden" data-testid="switch-to-login" className="text-accent font-bold hover:underline">Einloggen</Link></>
        )}
      </p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell>
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Passwort vergessen?</h1>
      <p className="text-sm text-stone-500 mt-1.5">Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.</p>
      {sent ? (
        <div className="mt-6" data-testid="forgot-sent-message">
          <div className="bg-[#FAF9F5] rounded-2xl p-4 text-sm flex gap-3">
            <MailCheck className="w-5 h-5 text-accent shrink-0" />
            Wenn ein Konto mit dieser E-Mail-Adresse existiert, haben wir dir einen Link zum Zurücksetzen gesendet.
          </div>
          <Link to="/anmelden" className="block text-center mt-5 mb-1 text-sm font-bold text-accent hover:underline">Zur Anmeldung</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><label className={label} htmlFor="fe">E-Mail-Adresse</label>
            <input id="fe" data-testid="forgot-email-input" className={input} type="email" placeholder="deine@email.de" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <button data-testid="forgot-submit-btn" disabled={busy} className={btn}>{busy ? "Link wird gesendet …" : "Link senden"}</button>
          <Link to="/anmelden" className="block text-center text-sm font-semibold text-muted-foreground hover:text-accent pb-1">Zurück zur Anmeldung</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Die Passwörter stimmen nicht überein.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Dein Passwort wurde erfolgreich geändert.", { description: "Bitte melde dich mit deinem neuen Passwort an." });
      nav("/anmelden");
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <AuthShell>
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Neues Passwort festlegen</h1>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className={label}>Neues Passwort</label>
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mindestens 8 Zeichen" testid="reset-password-input" autoComplete="new-password" />
          <PasswordStrength password={password} />
        </div>
        <div>
          <label className={label}>Passwort bestätigen</label>
          <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Passwort wiederholen" testid="reset-confirm-input" autoComplete="new-password" />
        </div>
        <button data-testid="reset-submit-btn" disabled={busy} className={btn}>{busy ? "Bitte warten …" : "Passwort speichern"}</button>
      </form>
    </AuthShell>
  );
}

const _verifyCache = {};

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const { user, refresh } = useAuth();
  useEffect(() => {
    const token = params.get("token");
    if (!token) return setStatus("notoken");
    if (!_verifyCache[token]) {
      _verifyCache[token] = api.post("/auth/verify-email", { token }).then(() => "ok").catch(() => "fail");
    }
    let active = true;
    _verifyCache[token].then((s) => {
      if (!active) return;
      setStatus(s);
      if (s === "ok") refresh?.();
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <AuthShell>
      <div className="text-center py-4">
        {status === "checking" && <><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-sm font-semibold">Bitte warten …</p></>}
        {status === "ok" && (
          <div data-testid="verify-success">
            <div className="w-16 h-16 bg-accent/10 text-accent rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8" /></div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-5">E-Mail bestätigt!</h1>
            <p className="text-sm text-muted-foreground mt-2">Dein KLEDIO Konto ist jetzt vollständig verifiziert.</p>
            <Link to={user ? "/" : "/anmelden"} className={`${btn} mt-6 mb-1 flex items-center justify-center`} data-testid="verify-continue-btn">{user ? "Weiter zu KLEDIO" : "Zur Anmeldung"}</Link>
          </div>
        )}
        {(status === "fail" || status === "notoken") && (
          <div data-testid="verify-fail">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Link ungültig oder abgelaufen</h1>
            <p className="text-sm text-muted-foreground mt-2">Fordere in deinen Einstellungen eine neue Bestätigungs-E-Mail an.</p>
            {user && <button data-testid="verify-resend-btn" onClick={async () => {
              try { await api.post("/auth/resend-verification"); toast.success("Eine neue Bestätigungs-E-Mail wurde gesendet."); }
              catch (e) { toast.error(errText(e)); }
            }} className={`${btn} mt-6 mb-1`}>E-Mail erneut senden</button>}
            {!user && <Link to="/anmelden" className={`${btn} mt-6 mb-1 flex items-center justify-center`}>Zur Anmeldung</Link>}
          </div>
        )}
      </div>
    </AuthShell>
  );
}
